import { useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  STACK_FAMILIES,
  familyOfTemplateName,
} from '@internal/plugin-stack-common';
import { brand, derived, onColour } from '../theme/palette';
import { useTemplates } from './templates';

export type KindIconProps = {
  className?: string;
  style?: React.CSSProperties;
};

export type Kind = {
  id: string;
  /** What this thing is, in the user's words, rather than its template identifier. */
  label: string;
  color: string;
  Icon: React.ComponentType<KindIconProps>;
};

const useMonogramStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: theme.shape.borderRadius,
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.3px',
    flex: '0 0 auto',
  },
}));

/**
 * A two-letter monogram in a coloured square, the way the prototype draws a
 * kind. Deliberately not a stock icon set: the families the platform ships are
 * too close together visually (four flavours of VM) for pictograms to tell
 * them apart, and the monogram scales to a new family without anyone having to
 * choose a glyph for it.
 */
const monogram =
  (glyph: string, color: string): React.ComponentType<KindIconProps> =>
  ({ className, style }: KindIconProps) => {
    const classes = useMonogramStyles();
    return (
      <span
        className={[classes.root, className].filter(Boolean).join(' ')}
        style={{ background: color, color: onColour(color), ...style }}
        aria-hidden
      >
        {glyph}
      </span>
    );
  };

/**
 * How a family is drawn. What a family *is* - its name, and which tabs its
 * page may carry - belongs to stack-common and is read from there, so the two
 * cannot disagree about what a `storage` component is called.
 */
const FAMILY_KINDS: Record<string, { glyph: string; color: string }> = {
  governance: { glyph: 'Pg', color: brand.navy },
  compute: { glyph: 'VM', color: brand.bronzeGreen },
  windows: { glyph: 'Wi', color: brand.navy },
  database: { glyph: 'Db', color: brand.wood },
  messaging: { glyph: 'Mq', color: brand.energyRed },
  kubernetes: { glyph: 'K8', color: brand.bronzeGreen },
  storage: { glyph: 'S3', color: brand.charcoal },
  volume: { glyph: 'Vo', color: brand.wood },
  network: { glyph: 'IP', color: derived.bronzeLight },
  'network-rules': { glyph: 'Fw', color: brand.charcoal },
  image: { glyph: 'Im', color: brand.sea },
  other: { glyph: '??', color: brand.sand },
};

const FAMILY_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    STACK_FAMILIES.map(family => [family.id, family.label]),
  ),
  governance: 'Policies and quotas',
};

const labelOfFamily = (familyId: string) =>
  FAMILY_LABELS[familyId] ?? 'Component';

/**
 * Per-template overrides, so the VM templates are not four identical
 * squares. Anything absent falls back to its family, which is what makes a
 * newly ingested template render sensibly without an edit here.
 */
const TEMPLATE_KINDS: Record<string, { glyph: string; color: string }> = {
  'application-group-policy': { glyph: 'Po', color: brand.navy },
  'application-group-quota': { glyph: 'Qu', color: brand.wood },
  'cloudstack-vm': { glyph: 'VM', color: brand.bronzeGreen },
  'azure-vm': { glyph: 'Az', color: derived.navySteel },
  'azure-windows-vm': { glyph: 'AW', color: derived.navySteel },
  'vm-service': { glyph: 'Sv', color: brand.sea },
  'windows-vm': { glyph: 'Wi', color: brand.navy },
  'k8s-cluster': { glyph: 'K8', color: brand.bronzeGreen },
  'object-storage': { glyph: 'S3', color: brand.charcoal },
  'cloudstack-volume': { glyph: 'Vo', color: brand.wood },
  'public-ip': { glyph: 'IP', color: derived.bronzeLight },
  'tenant-image': { glyph: 'Im', color: brand.sea },
  // Not a stack family: it makes an ApplicationGroup, which is the box the
  // stacks go in. It still gets a square rather than the "??" fallback, because
  // "??" reads as "the platform does not know what this is".
  'application-group': { glyph: 'Ag', color: derived.bronzeLight },
};

const cache = new Map<string, Kind>();

const makeKind = (
  id: string,
  label: string,
  glyph: string,
  color: string,
): Kind => {
  // The monogram component is memoised per kind because it is passed as a
  // component type: a fresh function on every render would remount the icon.
  const cached = cache.get(id);
  if (cached) return cached;
  const kind: Kind = { id, label, color, Icon: monogram(glyph, color) };
  cache.set(id, kind);
  return kind;
};

export const kindByFamilyId = (familyId: string): Kind => {
  const family = FAMILY_KINDS[familyId] ?? FAMILY_KINDS.other;
  return makeKind(
    familyId,
    labelOfFamily(familyId),
    family.glyph,
    family.color,
  );
};

export const kindByTemplateName = (
  templateName: string,
  familyId: string,
): Kind => {
  const override = TEMPLATE_KINDS[templateName];
  if (!override) return kindByFamilyId(familyId);
  return makeKind(
    `${familyId}:${templateName}`,
    labelOfFamily(familyId),
    override.glyph,
    override.color,
  );
};

/**
 * Resolves a Stack type identifier to its kind.
 *
 * A stack entity carries the template's name but not its tags, so the family
 * has to be looked up through the template catalog rather than derived from
 * the stack itself. Until that load finishes, and for a stack whose template
 * has since been withdrawn, the name is resolved against the families the
 * platform ships and only then falls back to the generic component kind - the
 * row still renders, and it renders as the right thing.
 */
export const useKindOfTemplateName = (): ((templateName: string) => Kind) => {
  const { value: templates } = useTemplates();

  return useMemo(() => {
    const familyByName = new Map(
      templates.map(template => [template.name, template.familyId]),
    );
    return (templateName: string) =>
      kindByTemplateName(
        templateName,
        familyByName.get(templateName) ??
          familyOfTemplateName(templateName)?.id ??
          'other',
      );
  }, [templates]);
};
